// ProgressBar — frame/audit progress track with drag-to-scrub. Spec §6.5.
//
// Reads `currentFrame` / `totalFrames` from the video slice and renders the
// playhead plus per-frame tick overlays. Press-and-drag anywhere on the track
// to scrub the playhead through `setCurrentFrame`, which snaps to the nearest
// picked frame when `navigableFrames` is active.
//
// Tick layers (mutually exclusive, by priority — modified wins over saved):
//   orange — frame currently has committed modifications
//   green  — frame saved and currently unmodified
//   grey   — picked frame neither modified nor saved

import * as React from 'react';
import { COLORS } from '../../config/constants';
import { t } from '../../config/strings';
import { formatDuration } from '../../lib/utils';
import { useStore } from '../../store';
import { editedFrameSet } from '../../store/edits';
import { episodeElapsedMs } from '../../store/video';

// Stat — one "Label: value" cell in the episode summary panel. The label is
// muted and the value emphasized so the numbers read at a glance.
function Stat({
  label,
  value,
  testId,
  swatch,
}: {
  label: string;
  value: React.ReactNode;
  testId?: string;
  swatch?: string;
}): React.ReactElement {
  return (
    <span className="flex items-baseline gap-1.5">
      {swatch && (
        <span
          aria-hidden
          className="h-3 w-3 self-center rounded-sm shrink-0"
          style={{ backgroundColor: swatch }}
        />
      )}
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums text-foreground" data-testid={testId}>
        {value}
      </span>
    </span>
  );
}

// EpisodeTimer — the running per-episode clock, shown as the first stat in the
// summary panel. Re-renders once a second off a local tick; the elapsed value
// is derived from the store (timerBaseMs + time since timerStartedAt).
function EpisodeTimer(): React.ReactElement {
  const timerBaseMs = useStore((s) => s.timerBaseMs);
  const timerStartedAt = useStore((s) => s.timerStartedAt);
  const [, forceTick] = React.useReducer((n: number) => n + 1, 0);

  React.useEffect(() => {
    if (timerStartedAt === null) return;
    const id = window.setInterval(() => forceTick(), 1000);
    return () => window.clearInterval(id);
  }, [timerStartedAt]);

  const elapsed = episodeElapsedMs({ timerBaseMs, timerStartedAt });
  return (
    <Stat label={t.progress.totalTime} value={formatDuration(elapsed)} testId="episode-timer" />
  );
}

export function ProgressBar(): React.ReactElement {
  const currentFrame = useStore((s) => s.currentFrame);
  const totalFrames = useStore((s) => s.totalFrames);
  const editsLeft = useStore((s) => s.editsLeft);
  const editsRight = useStore((s) => s.editsRight);
  const editsFrontLeft = useStore((s) => s.editsFrontLeft);
  const editsFrontRight = useStore((s) => s.editsFrontRight);
  const navigableFrames = useStore((s) => s.navigableFrames);
  const auditedFrames = useStore((s) => s.auditedFrames);
  const setCurrentFrame = useStore((s) => s.setCurrentFrame);

  const pct = totalFrames > 1 ? (currentFrame / (totalFrames - 1)) * 100 : 0;

  const trackRef = React.useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const seekToClientX = React.useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el || totalFrames <= 1) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      setCurrentFrame(Math.round(frac * (totalFrames - 1)));
    },
    [totalFrames, setCurrentFrame]
  );

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);
      seekToClientX(e.clientX);
    },
    [seekToClientX]
  );

  const handlePointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      seekToClientX(e.clientX);
    },
    [isDragging, seekToClientX]
  );

  const handlePointerUp = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setIsDragging(false);
  }, []);

  const editedFrames = React.useMemo(
    () =>
      Array.from(editedFrameSet({ editsLeft, editsRight, editsFrontLeft, editsFrontRight })).sort(
        (a, b) => a - b
      ),
    [editsLeft, editsRight, editsFrontLeft, editsFrontRight]
  );

  const modifiedFrames = editedFrames;

  const savedClean = React.useMemo(() => {
    const modified = new Set(modifiedFrames);
    return auditedFrames.filter((f) => !modified.has(f));
  }, [auditedFrames, modifiedFrames]);

  // Picked-but-untouched frames: grey ticks for sampled frames neither modified
  // nor saved. Empty unless picks are loaded.
  const pickedUnannotated = React.useMemo(() => {
    if (!navigableFrames || navigableFrames.length === 0) return [];
    const modified = new Set(modifiedFrames);
    const saved = new Set(auditedFrames);
    return navigableFrames.filter((f) => !modified.has(f) && !saved.has(f));
  }, [navigableFrames, modifiedFrames, auditedFrames]);

  // Index of the current frame within the pick list (null when no picks or the
  // current frame isn't a pick). Drives the "x/y picked" position read-out.
  const pickIndex = React.useMemo(() => {
    if (!navigableFrames || navigableFrames.length === 0) return null;
    const i = navigableFrames.indexOf(currentFrame);
    return i >= 0 ? i : null;
  }, [navigableFrames, currentFrame]);

  // Episode summary counts, split to mirror the three tick colors:
  //   savedCount       — green  (saved and currently unmodified)
  //   unsavedCount     — orange (currently modified, saved or not)
  //   unannotatedCount — grey   (picked, neither modified nor saved)
  // unsavedCount wins over savedCount, matching the tick
  // priority. Without picks the whole episode is the auditable set.
  const { totalPicked, savedCount, unsavedCount, unannotatedCount } = React.useMemo(() => {
    const inRange = (f: number): boolean => f >= 0 && f < totalFrames;
    const modified = new Set(modifiedFrames.filter(inRange));
    const saved = new Set(savedClean.filter(inRange));
    const picked =
      navigableFrames && navigableFrames.length > 0 ? navigableFrames.length : totalFrames;
    return {
      totalPicked: picked,
      savedCount: saved.size,
      unsavedCount: modified.size,
      unannotatedCount: Math.max(0, picked - saved.size - modified.size),
    };
  }, [navigableFrames, savedClean, modifiedFrames, totalFrames]);

  // Current position read-outs: "current/total" for both the whole episode and
  // the pick list (1-based so they read as "x of y").
  const frameValue = totalFrames > 0 ? `${currentFrame + 1}/${totalFrames}` : `0/0`;
  const pickValue =
    navigableFrames && navigableFrames.length > 0
      ? `${(pickIndex ?? 0) + 1}/${navigableFrames.length}`
      : `${totalPicked}`;

  return (
    <div
      ref={trackRef}
      className={`relative w-full select-none touch-none h-6 mb-14 ${
        isDragging ? 'cursor-grabbing' : 'cursor-pointer'
      }`}
      data-testid="progress-bar"
      role="progressbar"
      aria-label={t.progress.ariaLabel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div className="h-2 w-full rounded bg-muted absolute top-1/2 -translate-y-1/2" />
      {/* Episode summary panel: running timer + frame/pick/annotation counts.
          Sits just under the track (the track is lifted via mb to leave room). */}
      <div className="absolute top-full mt-5 left-1/2 -translate-x-1/2 flex items-center gap-3 whitespace-nowrap rounded-md border border-border bg-muted/40 px-4 py-1 text-base text-foreground pointer-events-none">
        <EpisodeTimer />
        <span className="h-4 w-px bg-border" />
        <Stat label={t.progress.totalFrames} value={frameValue} />
        <span className="h-4 w-px bg-border" />
        <Stat label={t.progress.totalPickedFrames} value={pickValue} />
        <span className="h-4 w-px bg-border" />
        <Stat
          swatch={COLORS.pickedTick}
          label={t.progress.unannotatedFrames}
          value={unannotatedCount}
        />
        <span className="h-4 w-px bg-border" />
        <Stat
          swatch={COLORS.unsavedTick}
          label={t.progress.annotatedUnsavedFrames}
          value={unsavedCount}
        />
        <span className="h-4 w-px bg-border" />
        <Stat
          swatch={COLORS.auditedTick}
          label={t.progress.annotatedSavedFrames}
          value={savedCount}
        />
      </div>
      {/* Picked-but-untouched ticks: grey vertical marks. */}
      {totalFrames > 1 &&
        pickedUnannotated
          .filter((frame) => frame !== currentFrame)
          .map((frame) => (
            <div
              key={`pick-${frame}`}
              data-testid={`pick-tick-${frame}`}
              data-frame={frame}
              className="absolute top-0 h-full w-0.5 -translate-x-1/2 rounded-sm pointer-events-none"
              style={{
                left: `${(frame / (totalFrames - 1)) * 100}%`,
                backgroundColor: COLORS.pickedTick,
              }}
            />
          ))}
      {/* Saved-and-clean ticks: green vertical marks. */}
      {totalFrames > 1 &&
        savedClean
          .filter((frame) => frame !== currentFrame)
          .map((frame) => (
            <div
              key={`audited-${frame}`}
              data-testid={`audited-tick-${frame}`}
              data-frame={frame}
              className="absolute top-0 h-full w-0.5 -translate-x-1/2 rounded-sm pointer-events-none"
              style={{
                left: `${(frame / (totalFrames - 1)) * 100}%`,
                backgroundColor: COLORS.auditedTick,
              }}
            />
          ))}
      {/* Modified ticks: orange vertical marks for every frame with committed edits. */}
      {totalFrames > 1 &&
        modifiedFrames
          .filter((frame) => frame !== currentFrame)
          .map((frame) => (
            <div
              key={frame}
              data-testid={`tick-${frame}`}
              data-frame={frame}
              className="absolute top-0 h-full w-0.5 -translate-x-1/2 rounded-sm pointer-events-none"
              style={{
                left: `${(frame / (totalFrames - 1)) * 100}%`,
                backgroundColor: COLORS.unsavedTick,
              }}
            />
          ))}
      {/* Current-frame marker: a thin, tall black tick so the playhead position
          stands out from the chunky colored ticks (hidden at this frame via the
          .filter calls above). */}
      <div
        data-testid="current-tick"
        className="absolute top-1/2 h-8 w-0.5 -translate-x-1/2 -translate-y-1/2 bg-foreground"
        style={{ left: `${pct}%` }}
      />
    </div>
  );
}
